import React from "react";
import { isEmpty } from "lodash";
import { useLocalStore, useObserver } from "mobx-react";
import {
  Paper,
  TableContainer,
  TableHead,
  TableRow,
  Table,
  TableCell,
  TableBody,
  Fab,
  makeStyles
} from "@material-ui/core";
import locale from "@bonliva-crm/locale";
import { Add } from "@material-ui/icons";
import { store } from "@store";
import {
  CustomerContractSubVersionEditorLocation,
  CONTRACT_CONTRACT_SUBVERSION_SLIDER
} from "@navigation";
import { SpinnerOverlay } from "@common";
import { listItem, SubVersionTableStore } from "./SubVersionTableStore";
import { SubVersionRow } from "./SubVersionRow";
import { ArchiveRow } from "@consultants/ConsultantContract/ContractsTable/consultant-contract-table-row";
import { CollapsibleRow } from "@consultants/ConsultantContract/ContractsTable/CollapsibleRow";
import { WithTooltip } from "@components";

type Props = {
  id: string;
  isNationalProcurement: boolean;
};

export const SubVersionTable: React.FC<Props> = ({
  id: CustomerContractId,
  isNationalProcurement
}) => {
  const classes = useStyles({});
  const tableStore = useLocalStore(() => new SubVersionTableStore(CustomerContractId));

  const handleEdit = (id: string) => {
    store().navigation.open(
      CONTRACT_CONTRACT_SUBVERSION_SLIDER,
      new CustomerContractSubVersionEditorLocation(CustomerContractId, id, "edit")
    );
  };

  const handleDuplicate = (id: string) => {
    store().navigation.open(
      CONTRACT_CONTRACT_SUBVERSION_SLIDER,
      new CustomerContractSubVersionEditorLocation(CustomerContractId, id, "duplicate")
    );
  };

  const handleDelete = (id: string) => {
    store().ui.confirmDelete(locale.deleteContract, (v) => {
      if (v) {
        tableStore.deleteFromList(id);
      }
    });
  };

  const handleAdd = () => {
    store().navigation.open(
      CONTRACT_CONTRACT_SUBVERSION_SLIDER,
      new CustomerContractSubVersionEditorLocation(CustomerContractId, null, "add")
    );
  };

  const renderList = (list: listItem[]) => {
    if (isEmpty(list)) {
      return (
        <TableRow>
          <TableCell colSpan={3} style={{ textAlign: "center" }}>
            {locale.NoContracts}
          </TableCell>
        </TableRow>
      );
    }

    return list.map((row) => {
      return (
        <SubVersionRow
          key={row._id}
          data={row}
          disabled={false}
          open={false}
          onDelete={() => handleDelete(row._id)}
          onEdit={() => handleEdit(row._id)}
          onDuplicate={() => handleDuplicate(row._id)}
        />
      );
    });
  };

  return useObserver(() => (
    <TableContainer component={Paper} className={classes.tableWrap}>
      <SpinnerOverlay show={tableStore.loading} />
      <Table
        aria-label={"snapshots"}
        className={classes.table}
        // padding={"none"}
        size={"small"}
      >
        <TableHead>
          <TableRow>
            {/*next row reserved for collaps arrow*/}
            <TableCell style={{ width: 30 }} />
            <TableCell style={{ width: "auto" }}>{locale.Name}</TableCell>
            {/*actions*/}
            <TableCell align={"right"} className={classes.actions}>
              <WithTooltip
                placement={"top"}
                title={locale.AddSubVersionDisableInfo}
                disableHoverListener={!isNationalProcurement}
              >
                <Fab
                  onClick={handleAdd}
                  color="primary"
                  disabled={isNationalProcurement}
                  size={"small"}
                >
                  <Add />
                </Fab>
              </WithTooltip>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {renderList(tableStore.actualItems)}

          {!isEmpty(tableStore.pastItems) && (
            <CollapsibleRow
              renderList={renderList}
              component={<ArchiveRow title={locale.Archived} />}
              defaultOpen={false}
              childContracts={tableStore.pastItems}
              childPadding={"0"}
              childBg={"#fff"}
            />
          )}
        </TableBody>
      </Table>
    </TableContainer>
  ));
};

const useStyles = makeStyles(() => ({
  tableWrap: {
    position: "relative",
    overflowX: "hidden",
    padding: 0
  },
  table: {
    tableLayout: "fixed",
    minHeight: 110
  },
  actions: {
    width: 160
  }
}));
